import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Link } from 'react-router-dom';
import { ThemeContext } from '../../context/ThemeContext';
import { LoadingState } from '../../components/common/LoadingState';
import { Card, Row, Col, Badge, Modal, Form, Spinner, Button, Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axiosInstance from '../../api/axios';
import { Endpoints } from '../../api/Endpoints';

const AgendaMonitor = () => {
    const { t } = useTranslation();
    const { isDark } = useContext(ThemeContext) || {};

    // State
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [currentDate, setCurrentDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [tipoRutaFilter, setTipoRutaFilter] = useState(''); // '' = Todas, 'preventa', 'merchandising'
    const [selectedRouteId, setSelectedRouteId] = useState('');
    const [selectedVendedorId, setSelectedVendedorId] = useState('');

    // Data from Backend
    const [semana, setSemana] = useState({ dias: [], formatted_rango: '', fecha_inicio: '', fecha_fin: '' });
    const [kpis, setKpis] = useState({
        total_programadas: 0,
        total_completadas: 0,
        total_en_curso: 0,
        total_pendientes: 0,
        total_efectivas: 0,
        efectividad_porcentaje: 0
    });
    const [rutasActivas, setRutasActivas] = useState([]);
    const [allRoutes, setAllRoutes] = useState([]);
    const [allResponsables, setAllResponsables] = useState([]);

    // Master Data: Motivos oficiales de no venta e incidencias
    const [motivosNoVenta, setMotivosNoVenta] = useState([]);

    // Modal state for selected visit detail
    const [selectedVisit, setSelectedVisit] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);

    // Supervisor actions state (Aprobar Justificación)
    const [approvingJustification, setApprovingJustification] = useState(false);

    // Lightbox modal state for photographic evidence
    const [previewPhotoUrl, setPreviewPhotoUrl] = useState(null);
    const [previewPhotoTitle, setPreviewPhotoTitle] = useState('Evidencia Fotográfica');

    // Fetch agenda data
    const fetchAgenda = useCallback(async (isRefresh = false) => {
        if (isRefresh) {
            setRefreshing(true);
        } else {
            setLoading(true);
        }

        try {
            const params = new URLSearchParams();
            if (currentDate) params.append('date', currentDate);
            if (tipoRutaFilter) params.append('tipo_ruta', tipoRutaFilter);
            if (selectedRouteId) params.append('route_id', selectedRouteId);
            if (selectedVendedorId) params.append('vendedor_id', selectedVendedorId);

            const endpoint = `${Endpoints.AGENDA_MONITOR || '/admin/routes/agenda-monitor'}?${params.toString()}`;
            const res = await axiosInstance.get(endpoint);

            if (res.data?.success) {
                const data = res.data.data;
                setSemana(data.semana || { dias: [] });
                setKpis(data.kpis || {});
                setRutasActivas(data.rutas_activas || []);
                if (Array.isArray(data.motivos_no_venta) && data.motivos_no_venta.length > 0) {
                    setMotivosNoVenta(data.motivos_no_venta);
                }
            }
        } catch (error) {
            console.error('Error cargando agenda de rutas:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [currentDate, tipoRutaFilter, selectedRouteId, selectedVendedorId]);

    // Initial catalogs
    useEffect(() => {
        const fetchCatalogs = async () => {
            try {
                const [routesRes, sellersRes, merchRes, reasonsRes] = await Promise.all([
                    axiosInstance.get('/admin/routes').catch(() => ({ data: { data: [] } })),
                    axiosInstance.get('/admin/sellers').catch(() => ({ data: { data: [] } })),
                    axiosInstance.get('/admin/merchandisers').catch(() => ({ data: { data: [] } })),
                    axiosInstance.get('/core/masters/no_sale_reasons').catch(() => ({ data: { data: [] } })),
                ]);

                const rList = Array.isArray(routesRes.data?.data) ? routesRes.data.data : [];
                setAllRoutes(rList);

                const sList = Array.isArray(sellersRes.data?.data) ? sellersRes.data.data : [];
                const mList = Array.isArray(merchRes.data?.data) ? merchRes.data.data : [];
                
                // Merge unique users
                const userMap = new Map();
                [...sList, ...mList].forEach(u => {
                    if (u && u.id && !userMap.has(u.id)) {
                        userMap.set(u.id, u);
                    }
                });
                setAllResponsables(Array.from(userMap.values()));

                const rReasons = Array.isArray(reasonsRes.data?.data) ? reasonsRes.data.data : [];
                if (rReasons.length > 0) {
                    setMotivosNoVenta(rReasons);
                }
            } catch (err) {
                console.error('Error cargando catálogos para agenda:', err);
            }
        };
        fetchCatalogs();
    }, []);

    // Fetch on filter or date change
    useEffect(() => {
        fetchAgenda();
    }, [fetchAgenda]);

    // Date navigation helpers
    const changeWeek = (daysOffset) => {
        const dateObj = new Date(currentDate);
        dateObj.setDate(dateObj.getDate() + daysOffset);
        setCurrentDate(dateObj.toISOString().split('T')[0]);
    };

    const goToCurrentWeek = () => {
        setCurrentDate(new Date().toISOString().split('T')[0]);
    };

    const isCurrentWeek = () => {
        const todayStr = new Date().toISOString().split('T')[0];
        if (!semana.fecha_inicio || !semana.fecha_fin) return true;
        return todayStr >= semana.fecha_inicio && todayStr <= semana.fecha_fin;
    };

    // Card click handler
    const handleVisitClick = (visit) => {
        setSelectedVisit(visit);
        setShowDetailModal(true);
    };

    // Supervisory actions handlers
    const handleApproveJustification = async (visitId) => {
        if (!visitId) return;
        setApprovingJustification(true);
        try {
            const res = await axiosInstance.post(`/admin/routes/visits/${visitId}/approve-justification`);
            if (res.data?.success) {
                toast.success(res.data.message || 'Justificación aprobada exitosamente');
                setSelectedVisit(prev => prev ? {
                    ...prev,
                    justificacion_aprobada: true,
                    justificacion_aprobada_by: res.data.data?.justificacion_aprobada_by,
                    justificacion_aprobada_at: res.data.data?.justificacion_aprobada_at
                } : null);
                fetchAgenda(true);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error al aprobar justificación');
        } finally {
            setApprovingJustification(false);
        }
    };

    // Helper para formatear de forma amigable y retrocompatible el motivo de no-venta
    const formatMotivoNoVenta = (motivo) => {
        if (!motivo) return '';
        const trimmed = String(motivo).trim();

        // 1. Mapeo para registros numéricos legados/MVP de la app móvil
        const legacyMap = {
            '1': 'Local Cerrado',
            '2': 'No tiene dinero',
            '3': 'Sin inventario propio',
            '4': 'No se encontró al encargado',
            '5': 'Rechazó el producto',
            '6': 'Otro (Especificado en observaciones)'
        };
        if (legacyMap[trimmed]) {
            return legacyMap[trimmed];
        }

        // 2. Mapeo para catálogo maestro de no-venta autogestionable
        if (Array.isArray(motivosNoVenta) && motivosNoVenta.length > 0) {
            const found = motivosNoVenta.find(m => 
                m.id === trimmed || 
                m.name === trimmed || 
                String(m.name).toUpperCase() === trimmed.toUpperCase() ||
                m.code === trimmed
            );
            if (found) {
                return found.value || found.name || trimmed;
            }
        }

        // 3. Códigos estándar conocidos
        const standardCodeMap = {
            'LOCAL_CERRADO': 'Local Cerrado',
            'SIN_STOCK': 'Stock suficiente / No necesita mercancía',
            'SIN_LIQUIDEZ': 'Sin liquidez de pago',
            'ENCARGADO_AUSENTE': 'No se encontró al encargado',
            'REMODELACION_INVENTARIO': 'Local en inventario interno o remodelación',
            'FALLA_SERVICIOS': 'Falla de servicios públicos (Luz / Agua)',
            'ACCESO_BLOQUEADO': 'Acceso o vía bloqueada / Clima',
            'REPROGRAMACION_CLIENTE': 'Cliente solicitó reprogramar visita',
            'HORARIO_NO_HABIL': 'Fuera de horario de recepción a proveedores',
            'FACTURAS_PENDIENTES': 'Facturas vencidas pendientes de pago'
        };
        if (standardCodeMap[trimmed.toUpperCase()]) {
            return standardCodeMap[trimmed.toUpperCase()];
        }

        return trimmed;
    };

    // Status styling helpers with optimized dark mode contrast
    const getStatusBadge = (visit) => {
        const estado = visit.estado;
        const resultado = visit.resultado_visita;

        if (estado === 'cancelada' || resultado === 'Visita Cancelada' || resultado === 'Cancelada') {
            return (
                <span 
                    className="badge rounded-pill d-inline-flex align-items-center gap-1" 
                    style={{ 
                        backgroundColor: isDark ? 'rgba(239, 68, 68, 0.22)' : 'rgba(239, 68, 68, 0.12)', 
                        color: isDark ? '#fca5a5' : '#dc2626', 
                        border: isDark ? '1px solid rgba(252, 165, 165, 0.45)' : '1px solid rgba(239, 68, 68, 0.3)', 
                        fontSize: '0.72rem', 
                        padding: '4px 9px',
                        fontWeight: '600'
                    }}
                >
                    <i className="mdi mdi-cancel"></i> Cancelada
                </span>
            );
        }

        if (estado === 'completada') {
            if (resultado === 'Venta Exitosa') {
                return (
                    <span 
                        className="badge rounded-pill d-inline-flex align-items-center gap-1" 
                        style={{ 
                            backgroundColor: isDark ? 'rgba(16, 185, 129, 0.22)' : 'rgba(16, 185, 129, 0.12)', 
                            color: isDark ? '#6ee7b7' : '#059669', 
                            border: isDark ? '1px solid rgba(110, 231, 183, 0.45)' : '1px solid rgba(16, 185, 129, 0.3)', 
                            fontSize: '0.72rem', 
                            padding: '4px 9px',
                            fontWeight: '600'
                        }}
                    >
                        <i className="mdi mdi-cart-check"></i> Venta Exitosa
                    </span>
                );
            }
            if (resultado === 'Auditoría Completada') {
                return (
                    <span 
                        className="badge rounded-pill d-inline-flex align-items-center gap-1" 
                        style={{ 
                            backgroundColor: isDark ? 'rgba(168, 85, 247, 0.22)' : 'rgba(124, 58, 237, 0.12)', 
                            color: isDark ? '#d8b4fe' : '#7c3aed', 
                            border: isDark ? '1px solid rgba(216, 180, 254, 0.45)' : '1px solid rgba(124, 58, 237, 0.3)', 
                            fontSize: '0.72rem', 
                            padding: '4px 9px',
                            fontWeight: '600'
                        }}
                    >
                        <i className="mdi mdi-store"></i> Auditoría OK
                    </span>
                );
            }
            if (resultado === 'Local Cerrado') {
                return (
                    <span 
                        className="badge rounded-pill d-inline-flex align-items-center gap-1" 
                        style={{ 
                            backgroundColor: isDark ? 'rgba(245, 158, 11, 0.22)' : 'rgba(245, 158, 11, 0.12)', 
                            color: isDark ? '#fde047' : '#b45309', 
                            border: isDark ? '1px solid rgba(253, 224, 71, 0.45)' : '1px solid rgba(245, 158, 11, 0.3)', 
                            fontSize: '0.72rem', 
                            padding: '4px 9px',
                            fontWeight: '600'
                        }}
                    >
                        <i className="mdi mdi-store-off"></i> Local Cerrado
                    </span>
                );
            }
            if (resultado === 'No Compra') {
                return (
                    <span 
                        className="badge rounded-pill d-inline-flex align-items-center gap-1" 
                        style={{ 
                            backgroundColor: isDark ? 'rgba(249, 115, 22, 0.22)' : 'rgba(234, 88, 12, 0.12)', 
                            color: isDark ? '#fdba74' : '#c2410c', 
                            border: isDark ? '1px solid rgba(253, 186, 116, 0.45)' : '1px solid rgba(234, 88, 12, 0.3)', 
                            fontSize: '0.72rem', 
                            padding: '4px 9px',
                            fontWeight: '600'
                        }}
                    >
                        <i className="mdi mdi-cart-remove"></i> No Compra
                    </span>
                );
            }
            return (
                <span 
                    className="badge rounded-pill d-inline-flex align-items-center gap-1" 
                    style={{ 
                        backgroundColor: isDark ? 'rgba(16, 185, 129, 0.18)' : 'rgba(16, 185, 129, 0.1)', 
                        color: isDark ? '#6ee7b7' : '#059669', 
                        border: isDark ? '1px solid rgba(110, 231, 183, 0.35)' : '1px solid rgba(16, 185, 129, 0.3)', 
                        fontSize: '0.72rem', 
                        padding: '4px 9px',
                        fontWeight: '600'
                    }}
                >
                    <i className="mdi mdi-check-circle-outline"></i> Completada
                </span>
            );
        }

        if (estado === 'en_curso') {
            return (
                <span 
                    className="badge rounded-pill d-inline-flex align-items-center gap-1" 
                    style={{ 
                        backgroundColor: isDark ? 'rgba(59, 130, 246, 0.25)' : 'rgba(59, 130, 246, 0.12)', 
                        color: isDark ? '#93c5fd' : '#1d4ed8', 
                        border: isDark ? '1px solid rgba(147, 197, 253, 0.5)' : '1px solid rgba(59, 130, 246, 0.3)', 
                        fontSize: '0.72rem', 
                        padding: '4px 9px',
                        fontWeight: '600'
                    }}
                >
                    <span className="spinner-grow spinner-grow-sm me-1" style={{ width: '8px', height: '8px' }} role="status"></span>
                    En Sitio ({visit.check_in_time})
                </span>
            );
        }

        if (estado === 'no_realizada' || resultado === 'No Realizada' || (visit.is_past && (estado === 'pendiente' || !estado))) {
            return (
                <span 
                    className="badge rounded-pill d-inline-flex align-items-center gap-1" 
                    style={{ 
                        backgroundColor: isDark ? 'rgba(239, 68, 68, 0.22)' : 'rgba(239, 68, 68, 0.12)', 
                        color: isDark ? '#fca5a5' : '#dc2626', 
                        border: isDark ? '1px solid rgba(252, 165, 165, 0.45)' : '1px solid rgba(239, 68, 68, 0.3)', 
                        fontSize: '0.72rem', 
                        padding: '4px 9px',
                        fontWeight: '600'
                    }}
                >
                    <i className="mdi mdi-alert-circle-outline"></i> No Realizada
                </span>
            );
        }

        return (
            <span 
                className="badge rounded-pill d-inline-flex align-items-center gap-1" 
                style={{ 
                    backgroundColor: isDark ? 'rgba(148, 163, 184, 0.15)' : 'rgba(100, 116, 139, 0.1)', 
                    color: isDark ? '#cbd5e1' : '#64748b', 
                    border: isDark ? '1px solid rgba(148, 163, 184, 0.3)' : '1px solid #cbd5e1', 
                    fontSize: '0.72rem', 
                    padding: '4px 9px',
                    fontWeight: '500'
                }}
            >
                <i className="mdi mdi-clock-outline"></i> Pendiente
            </span>
        );
    };

    return (
        <div>
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />

            {/* Quick Navigation Stepper / Flow Breadcrumbs */}
            <div 
                className="d-flex flex-wrap align-items-center justify-content-between mb-4 p-2 px-3 rounded shadow-sm" 
                style={{ 
                    backgroundColor: isDark ? '#161e38' : '#ffffff', 
                    border: isDark ? '1px solid #2a3759' : '1px solid #e2e8f0' 
                }}
            >
                <div className="d-flex align-items-center gap-2 small flex-wrap">
                    <span className="text-muted fw-bold me-1 text-uppercase" style={{ fontSize: '0.72rem', letterSpacing: '0.5px' }}>
                        Flujo Logístico:
                    </span>
                    <Link 
                        to="/general-pages/zones-manager" 
                        className="text-decoration-none d-inline-flex align-items-center gap-1 px-2 py-1 rounded" 
                        style={{ 
                            color: isDark ? '#93c5fd' : '#183eeb', 
                            backgroundColor: isDark ? 'rgba(59, 130, 246, 0.15)' : '#eff6ff',
                            fontWeight: '600',
                            fontSize: '0.78rem'
                        }}
                    >
                        <i className="mdi mdi-map-legend"></i> 1. Zonas y Territorios
                    </Link>
                    <i className="mdi mdi-chevron-right text-muted"></i>
                    <Link 
                        to="/general-pages/route-planner" 
                        className="text-decoration-none d-inline-flex align-items-center gap-1 px-2 py-1 rounded" 
                        style={{ 
                            color: isDark ? '#93c5fd' : '#183eeb', 
                            backgroundColor: isDark ? 'rgba(59, 130, 246, 0.15)' : '#eff6ff',
                            fontWeight: '600',
                            fontSize: '0.78rem'
                        }}
                    >
                        <i className="mdi mdi-calendar-clock"></i> 2. Planificador Semanal
                    </Link>
                    <i className="mdi mdi-chevron-right text-muted"></i>
                    <span 
                        className="fw-bold d-inline-flex align-items-center gap-1 px-2 py-1 rounded shadow-sm" 
                        style={{ 
                            color: '#ffffff', 
                            backgroundColor: '#183eeb',
                            fontSize: '0.78rem'
                        }}
                    >
                        <i className="mdi mdi-monitor-dashboard"></i> 3. Monitoreo en Vivo
                    </span>
                </div>

                <div className="d-flex align-items-center gap-2 mt-2 mt-md-0">
                    <Link 
                        to="/general-pages/route-planner" 
                        className="btn btn-sm" 
                        style={{ 
                            fontSize: '0.78rem',
                            fontWeight: '600',
                            borderRadius: '8px',
                            color: isDark ? '#93c5fd' : '#183eeb',
                            border: isDark ? '1px solid #3b82f6' : '1px solid #cbd5e1'
                        }}
                    >
                        <i className="mdi mdi-calendar-edit me-1"></i> Ajustar Planificación
                    </Link>
                </div>
            </div>

            {/* Header */}
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                <div>
                    <h4 className="card-title mb-1" style={{ color: isDark ? '#ffffff' : '#010028', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <i className="mdi mdi-calendar-multiselect" style={{ color: '#183eeb' }}></i>
                        {t('agenda_monitor_title', 'Agenda y Monitoreo de Rutas')}
                    </h4>
                    <p className="text-muted small mb-0">
                        {t('agenda_monitor_subtitle', 'Supervisión semanal de visitas, clientes agendados y estatus de ejecución en tiempo real.')}
                    </p>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <button 
                        onClick={() => fetchAgenda(true)} 
                        className="btn btn-outline-primary px-3 py-2" 
                        style={{ borderRadius: '10px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}
                        disabled={refreshing}
                        title="Actualizar datos en vivo"
                    >
                        <i className={`mdi mdi-refresh ${refreshing ? 'mdi-spin' : ''}`}></i>
                        {refreshing ? t('updating', 'Actualizando...') : t('btn_refresh_live', 'Actualizar en Vivo')}
                    </button>
                </div>
            </div>

            {/* KPI Cards Row - 6 Responsive Cards */}
            <Row className="g-3 mb-4">
                <Col xs={6} sm={4} xl={2}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '14px', backgroundColor: isDark ? '#161e38' : '#ffffff', border: isDark ? '1px solid #233052' : 'none' }}>
                        <Card.Body className="p-3 d-flex align-items-center">
                            <div className="rounded-circle d-flex align-items-center justify-content-center me-2 flex-shrink-0" style={{ width: '42px', height: '42px', backgroundColor: 'rgba(24, 62, 235, 0.12)', color: '#183eeb', fontSize: '1.2rem' }}>
                                <i className="mdi mdi-calendar-check"></i>
                            </div>
                            <div className="overflow-hidden">
                                <div className="text-muted small fw-semibold text-truncate">{t('kpi_scheduled_visits', 'Programadas')}</div>
                                <h4 className="mb-0 fw-bold" style={{ color: isDark ? '#ffffff' : '#010028' }}>{kpis.total_programadas}</h4>
                                <small className="text-muted" style={{ fontSize: '0.68rem' }}>Semana</small>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={6} sm={4} xl={2}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '14px', backgroundColor: isDark ? '#161e38' : '#ffffff', border: isDark ? '1px solid #233052' : 'none' }}>
                        <Card.Body className="p-3 d-flex align-items-center">
                            <div className="rounded-circle d-flex align-items-center justify-content-center me-2 flex-shrink-0" style={{ width: '42px', height: '42px', backgroundColor: 'rgba(16, 185, 129, 0.12)', color: '#10b981', fontSize: '1.2rem' }}>
                                <i className="mdi mdi-checkbox-marked-circle-outline"></i>
                            </div>
                            <div className="overflow-hidden">
                                <div className="text-muted small fw-semibold text-truncate">{t('kpi_effective_visits', 'Efectivas')}</div>
                                <h4 className="mb-0 fw-bold" style={{ color: '#10b981' }}>{kpis.total_efectivas}</h4>
                                <small className="text-muted" style={{ fontSize: '0.68rem' }}>Venta / Auditoría</small>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={6} sm={4} xl={2}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '14px', backgroundColor: isDark ? '#161e38' : '#ffffff', border: isDark ? '1px solid #233052' : 'none' }}>
                        <Card.Body className="p-3 d-flex align-items-center">
                            <div className="rounded-circle d-flex align-items-center justify-content-center me-2 flex-shrink-0" style={{ width: '42px', height: '42px', backgroundColor: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b', fontSize: '1.2rem' }}>
                                <i className="mdi mdi-alert-circle-outline"></i>
                            </div>
                            <div className="overflow-hidden">
                                <div className="text-muted small fw-semibold text-truncate">{t('kpi_no_effective_visits', 'No Efectivas / Sin Visita')}</div>
                                <h4 className="mb-0 fw-bold" style={{ color: '#f59e0b' }}>{(kpis.total_no_efectivas || 0) + (kpis.total_no_realizadas || 0)}</h4>
                                <small className="text-muted" style={{ fontSize: '0.68rem' }}>
                                    {kpis.total_no_realizadas ? `${kpis.total_no_realizadas} no realizadas` : 'Cerrado / No compra'}
                                </small>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={6} sm={4} xl={2}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '14px', backgroundColor: isDark ? '#161e38' : '#ffffff', border: isDark ? '1px solid #233052' : 'none' }}>
                        <Card.Body className="p-3 d-flex align-items-center">
                            <div className="rounded-circle d-flex align-items-center justify-content-center me-2 flex-shrink-0" style={{ width: '42px', height: '42px', backgroundColor: 'rgba(239, 68, 68, 0.12)', color: '#ef4444', fontSize: '1.2rem' }}>
                                <i className="mdi mdi-cancel"></i>
                            </div>
                            <div className="overflow-hidden">
                                <div className="text-muted small fw-semibold text-truncate">{t('kpi_cancelled_visits', 'Canceladas')}</div>
                                <h4 className="mb-0 fw-bold" style={{ color: '#ef4444' }}>{kpis.total_canceladas || 0}</h4>
                                <small className="text-muted" style={{ fontSize: '0.68rem' }}>Reprogramadas</small>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={6} sm={4} xl={2}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '14px', backgroundColor: isDark ? '#161e38' : '#ffffff', border: isDark ? '1px solid #233052' : 'none' }}>
                        <Card.Body className="p-3 d-flex align-items-center">
                            <div className="rounded-circle d-flex align-items-center justify-content-center me-2 flex-shrink-0" style={{ width: '42px', height: '42px', backgroundColor: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6', fontSize: '1.2rem' }}>
                                <i className="mdi mdi-map-marker-radius"></i>
                            </div>
                            <div className="overflow-hidden">
                                <div className="text-muted small fw-semibold text-truncate">{t('kpi_in_progress_visits', 'En Sitio')}</div>
                                <h4 className="mb-0 fw-bold" style={{ color: '#3b82f6' }}>{kpis.total_en_curso}</h4>
                                <small className="text-muted" style={{ fontSize: '0.68rem' }}>En curso ahora</small>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={6} sm={4} xl={2}>
                    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '14px', backgroundColor: isDark ? '#161e38' : '#ffffff', border: isDark ? '1px solid #233052' : 'none' }}>
                        <Card.Body className="p-3 d-flex align-items-center">
                            <div className="rounded-circle d-flex align-items-center justify-content-center me-2 flex-shrink-0" style={{ width: '42px', height: '42px', backgroundColor: 'rgba(124, 58, 237, 0.12)', color: '#7c3aed', fontSize: '1.2rem' }}>
                                <i className="mdi mdi-trending-up"></i>
                            </div>
                            <div className="overflow-hidden">
                                <div className="text-muted small fw-semibold text-truncate">{t('kpi_effectiveness', 'Efectividad')}</div>
                                <h4 className="mb-0 fw-bold" style={{ color: '#7c3aed' }}>{kpis.efectividad_porcentaje}%</h4>
                                <small className="text-muted" style={{ fontSize: '0.68rem' }}>Completadas</small>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Filter and Week Navigation Card */}
            <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: '16px', backgroundColor: isDark ? '#161e38' : '#ffffff', border: isDark ? '1px solid #233052' : 'none' }}>
                <div className="card-body p-3">
                    <Row className="g-3 align-items-center">
                        {/* Week Navigator */}
                        <Col lg={5} md={12}>
                            <div className="d-flex align-items-center gap-2">
                                <button 
                                    onClick={() => changeWeek(-7)} 
                                    className="btn btn-sm btn-outline-secondary px-2 py-1"
                                    style={{ borderRadius: '8px' }}
                                    title="Semana anterior"
                                >
                                    <i className="mdi mdi-chevron-left fs-5"></i>
                                </button>

                                <div className="text-center flex-grow-1 px-2 py-1 rounded" style={{ backgroundColor: isDark ? '#10172d' : '#f8faff', border: isDark ? '1px solid #2a3759' : '1px solid #e2e8f0' }}>
                                    <span className="fw-bold" style={{ fontSize: '0.9rem', color: isDark ? '#ffffff' : '#010028' }}>
                                        <i className="mdi mdi-calendar-range me-1 text-primary"></i>
                                        {semana.formatted_rango || 'Cargando semana...'}
                                    </span>
                                </div>

                                <button 
                                    onClick={() => changeWeek(7)} 
                                    className="btn btn-sm btn-outline-secondary px-2 py-1"
                                    style={{ borderRadius: '8px' }}
                                    title="Semana siguiente"
                                >
                                    <i className="mdi mdi-chevron-right fs-5"></i>
                                </button>

                                {!isCurrentWeek() && (
                                    <button 
                                        onClick={goToCurrentWeek}
                                        className="btn btn-sm btn-outline-primary text-nowrap"
                                        style={{ borderRadius: '8px', fontSize: '0.8rem' }}
                                    >
                                        {t('current_week', 'Semana Actual')}
                                    </button>
                                )}
                            </div>
                        </Col>

                        {/* Route Type Tabs */}
                        <Col lg={3} md={6}>
                            <div className="d-flex p-1 rounded" style={{ backgroundColor: isDark ? '#10172d' : '#f1f5f9' }}>
                                <button
                                    type="button"
                                    onClick={() => setTipoRutaFilter('')}
                                    className={`btn btn-sm flex-fill ${tipoRutaFilter === '' ? 'btn-primary' : 'btn-link text-muted'}`}
                                    style={{ borderRadius: '6px', fontSize: '0.78rem', fontWeight: tipoRutaFilter === '' ? 'bold' : 'normal', textDecoration: 'none' }}
                                >
                                    {t('all', 'Todas')}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setTipoRutaFilter('preventa')}
                                    className={`btn btn-sm flex-fill ${tipoRutaFilter === 'preventa' ? 'btn-primary' : 'btn-link text-muted'}`}
                                    style={{ borderRadius: '6px', fontSize: '0.78rem', fontWeight: tipoRutaFilter === 'preventa' ? 'bold' : 'normal', textDecoration: 'none' }}
                                >
                                    Preventa
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setTipoRutaFilter('merchandising')}
                                    className={`btn btn-sm flex-fill ${tipoRutaFilter === 'merchandising' ? 'btn-primary' : 'btn-link text-muted'}`}
                                    style={{ borderRadius: '6px', fontSize: '0.78rem', fontWeight: tipoRutaFilter === 'merchandising' ? 'bold' : 'normal', textDecoration: 'none' }}
                                >
                                    Trade
                                </button>
                            </div>
                        </Col>

                        {/* Route Dropdown Selector */}
                        <Col lg={2} md={3}>
                            <Form.Select 
                                value={selectedRouteId} 
                                onChange={e => setSelectedRouteId(e.target.value)}
                                className="customer-filter-control w-100 shadow-none cursor-pointer"
                                style={{ fontSize: '0.82rem' }}
                            >
                                <option value="">{t('all_routes_option', 'Todas las Rutas')}</option>
                                {allRoutes
                                    .filter(r => !tipoRutaFilter || r.tipo_ruta === tipoRutaFilter)
                                    .map(r => (
                                        <option key={r.id} value={r.id}>{r.nombre_ruta}</option>
                                    ))
                                }
                            </Form.Select>
                        </Col>

                        {/* Responsible Dropdown Selector */}
                        <Col lg={2} md={3}>
                            <Form.Select 
                                value={selectedVendedorId} 
                                onChange={e => setSelectedVendedorId(e.target.value)}
                                className="customer-filter-control w-100 shadow-none cursor-pointer"
                                style={{ fontSize: '0.82rem' }}
                            >
                                <option value="">{t('all_responsibles_option', 'Todos los Responsables')}</option>
                                {allResponsables.map(u => (
                                    <option key={u.id} value={u.id}>{u.nombre_completo} ({u.rol})</option>
                                ))}
                            </Form.Select>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Weekly Kanban Board */}
            {loading ? (
                <div className="card shadow-sm border-0 p-5 text-center" style={{ borderRadius: '16px', backgroundColor: isDark ? '#161e38' : '#ffffff' }}>
                    <LoadingState title={t('loading_agenda', 'Cargando agenda semanal de rutas...')} description={t('loading_agenda_sub', 'Obteniendo visitas, clientes agendados y estatus en tiempo real')} minHeight="250px" />
                </div>
            ) : (
                <div style={{ overflowX: 'auto', paddingBottom: '20px' }}>
                    <div className="d-flex gap-3" style={{ minWidth: '1200px' }}>
                        {(semana.dias || []).map(dia => {
                            const isToday = dia.is_today;
                            return (
                                <div 
                                    key={dia.day_number}
                                    className="flex-fill"
                                    style={{ 
                                        minWidth: '220px',
                                        maxWidth: '280px',
                                        borderRadius: '14px',
                                        backgroundColor: isDark 
                                            ? (isToday ? '#131c38' : '#0d1326') 
                                            : (isToday ? '#f0f5ff' : '#f8fafd'),
                                        border: isToday 
                                            ? (isDark ? '2px solid #3b82f6' : '2px solid #183eeb') 
                                            : (isDark ? '1px solid #1f2a47' : '1px solid #e2e8f0'),
                                        display: 'flex',
                                        flexDirection: 'column',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    {/* Column Header */}
                                    <div 
                                        className="p-3 border-bottom d-flex align-items-center justify-content-between"
                                        style={{ 
                                            borderColor: isToday ? (isDark ? '#3b82f6' : '#183eeb') : (isDark ? '#1f2a47' : '#e2e8f0'),
                                            borderTopLeftRadius: '12px',
                                            borderTopRightRadius: '12px',
                                            backgroundColor: isToday 
                                                ? (isDark ? '#19254d' : '#e6edff') 
                                                : (isDark ? '#11182e' : '#ffffff')
                                        }}
                                    >
                                        <div>
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="fw-bold" style={{ color: isDark ? '#ffffff' : '#010028', fontSize: '0.95rem' }}>
                                                    {dia.name}
                                                </span>
                                                {isToday && (
                                                    <span className="badge rounded-pill bg-primary" style={{ fontSize: '0.65rem', padding: '2px 6px' }}>
                                                        HOY
                                                    </span>
                                                )}
                                            </div>
                                            <small className="text-muted" style={{ fontSize: '0.75rem' }}>{dia.formatted_date}</small>
                                        </div>
                                        <span 
                                            className="badge rounded-pill"
                                            style={{ 
                                                backgroundColor: dia.visits.length > 0 ? (isToday ? '#183eeb' : '#64748b') : '#94a3b8',
                                                color: '#ffffff',
                                                fontSize: '0.75rem'
                                            }}
                                        >
                                            {dia.visits.length}
                                        </span>
                                    </div>

                                    {/* Column Body: Visit Cards */}
                                    <div className="p-2 d-flex flex-column gap-2 flex-grow-1" style={{ minHeight: '350px' }}>
                                        {dia.visits.length === 0 ? (
                                            <div className="text-center py-5 text-muted small my-auto">
                                                <i className="mdi mdi-calendar-blank-outline d-block fs-3 mb-1 opacity-50"></i>
                                                Sin visitas programadas
                                            </div>
                                        ) : (
                                            dia.visits.map((visit, vIdx) => {
                                                const isMerch = visit.tipo_ruta === 'merchandising';
                                                return (
                                                    <div 
                                                        key={visit.id}
                                                        onClick={() => handleVisitClick(visit)}
                                                        className="p-3 rounded shadow-sm cursor-pointer"
                                                        style={{ 
                                                            cursor: 'pointer',
                                                            backgroundColor: isDark ? '#1a233f' : '#ffffff',
                                                            border: isDark ? '1px solid #2d3b66' : '1px solid #e2e8f0',
                                                            borderRadius: '11px',
                                                            transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease'
                                                        }}
                                                        onMouseEnter={e => {
                                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                                            e.currentTarget.style.boxShadow = isDark ? '0 8px 16px rgba(0,0,0,0.35)' : '0 6px 12px rgba(0,0,0,0.08)';
                                                            if (isDark) e.currentTarget.style.borderColor = '#3b4d82';
                                                        }}
                                                        onMouseLeave={e => {
                                                            e.currentTarget.style.transform = 'translateY(0)';
                                                            e.currentTarget.style.boxShadow = '';
                                                            if (isDark) e.currentTarget.style.borderColor = '#2d3b66';
                                                        }}
                                                    >
                                                        {/* Top Row: Secuencia & Badges */}
                                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                                            <span 
                                                                className="badge rounded-pill" 
                                                                style={{ 
                                                                    fontSize: '0.7rem',
                                                                    backgroundColor: isDark ? '#26345a' : '#f1f5f9',
                                                                    color: isDark ? '#cbd5e1' : '#334155',
                                                                    border: isDark ? '1px solid #3b4d7e' : '1px solid #cbd5e1',
                                                                    fontWeight: '700',
                                                                    padding: '3px 7px'
                                                                }}
                                                            >
                                                                #{visit.secuencia || vIdx + 1}
                                                            </span>
                                                            <span 
                                                                className="badge rounded-pill d-inline-flex align-items-center gap-1" 
                                                                style={{ 
                                                                    fontSize: '0.68rem',
                                                                    backgroundColor: isMerch 
                                                                        ? (isDark ? 'rgba(168, 85, 247, 0.22)' : 'rgba(124, 58, 237, 0.1)') 
                                                                        : (isDark ? 'rgba(59, 130, 246, 0.22)' : 'rgba(24, 62, 235, 0.1)'),
                                                                    color: isMerch 
                                                                        ? (isDark ? '#d8b4fe' : '#7c3aed') 
                                                                        : (isDark ? '#93c5fd' : '#183eeb'),
                                                                    border: isMerch 
                                                                        ? (isDark ? '1px solid rgba(216, 180, 254, 0.45)' : '1px solid rgba(124, 58, 237, 0.3)') 
                                                                        : (isDark ? '1px solid rgba(147, 197, 253, 0.45)' : '1px solid rgba(24, 62, 235, 0.3)'),
                                                                    fontWeight: '600',
                                                                    padding: '3px 8px'
                                                                }}
                                                            >
                                                                <i className={`mdi ${isMerch ? 'mdi-store' : 'mdi-account-tie'}`}></i>
                                                                {isMerch ? 'Trade' : 'Preventa'}
                                                            </span>
                                                        </div>

                                                        {/* Customer Name */}
                                                        <div className="fw-bold text-truncate mb-1" style={{ fontSize: '0.92rem', color: isDark ? '#ffffff' : '#010028' }} title={visit.cliente.nombre_comercial}>
                                                            {visit.cliente.nombre_comercial}
                                                        </div>
                                                        <div className="small mb-2 d-flex align-items-center justify-content-between" style={{ fontSize: '0.74rem' }}>
                                                            <span style={{ color: isDark ? '#94a3b8' : '#64748b' }}>{visit.cliente.rif_cedula}</span>
                                                            {visit.cliente.zona_codigo && (
                                                                <span 
                                                                    className="badge rounded-pill d-inline-flex align-items-center gap-1" 
                                                                    style={{ 
                                                                        fontSize: '0.65rem',
                                                                        backgroundColor: isDark ? 'rgba(148, 163, 184, 0.18)' : '#f1f5f9',
                                                                        color: isDark ? '#cbd5e1' : '#475569',
                                                                        border: isDark ? '1px solid rgba(148, 163, 184, 0.35)' : '1px solid #cbd5e1',
                                                                        fontWeight: '600',
                                                                        padding: '2px 7px'
                                                                    }}
                                                                >
                                                                    <i className="mdi mdi-map-marker-outline"></i>
                                                                    {visit.cliente.zona_codigo}
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* Responsible & Route */}
                                                        <div className="small mb-2 d-flex align-items-center gap-1" style={{ fontSize: '0.75rem', color: isDark ? '#cbd5e1' : '#64748b' }}>
                                                            <i className="mdi mdi-account" style={{ color: isDark ? '#94a3b8' : '#94a3b8' }}></i>
                                                            <span className="text-truncate">{visit.responsable.nombre_completo || 'Sin Asignar'}</span>
                                                        </div>

                                                        {/* Motivo Oficial de Incidencia / No Venta */}
                                                        {visit.motivo_no_compra && (
                                                            <div 
                                                                className="text-truncate mb-2 px-2 py-1 rounded d-flex align-items-center gap-1" 
                                                                style={{ 
                                                                    fontSize: '0.7rem', 
                                                                    backgroundColor: isDark ? 'rgba(239, 68, 68, 0.16)' : 'rgba(239, 68, 68, 0.08)',
                                                                    color: isDark ? '#fca5a5' : '#b91c1c',
                                                                    border: isDark ? '1px solid rgba(239, 68, 68, 0.35)' : '1px solid rgba(239, 68, 68, 0.2)',
                                                                    fontWeight: '600'
                                                                }} 
                                                                title={`Motivo Oficial: ${formatMotivoNoVenta(visit.motivo_no_compra)}`}
                                                            >
                                                                <i className="mdi mdi-alert-circle-outline"></i>
                                                                <span className="text-truncate">{formatMotivoNoVenta(visit.motivo_no_compra)}</span>
                                                            </div>
                                                        )}

                                                        {/* Status Badge */}
                                                        <div className="d-flex align-items-center justify-content-between pt-2 border-top" style={{ borderColor: isDark ? 'rgba(148, 163, 184, 0.18)' : '#f1f5f9' }}>
                                                            {getStatusBadge(visit)}
                                                            {visit.duracion_minutos && (
                                                                <small className="fw-semibold d-flex align-items-center gap-1" style={{ fontSize: '0.72rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                                                                    <i className="mdi mdi-timer-outline"></i>
                                                                    {visit.duracion_minutos} min
                                                                </small>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Visit Detail Modal */}
            <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered size="md">
                <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: isDark ? '#10172d' : '#00024f', color: '#ffffff', borderBottom: '3px solid #183eeb' }}>
                    <Modal.Title style={{ color: '#ffffff', fontWeight: 'bold', fontSize: '1.1rem' }}>
                        <i className="mdi mdi-information-outline me-2"></i>
                        {t('visit_detail_title', 'Detalle de Visita Agendada')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4" style={{ backgroundColor: isDark ? '#161e38' : '#ffffff', color: isDark ? '#f8fafc' : '#010028' }}>
                    {selectedVisit && (
                        <div>
                            {/* Customer Profile Header */}
                            <div className="p-3 rounded mb-3" style={{ backgroundColor: isDark ? '#10172d' : '#f8faff', border: isDark ? '1px solid #2a3759' : '1px solid #e2e8f0' }}>
                                <div className="fw-bold fs-6 mb-1" style={{ color: isDark ? '#ffffff' : '#010028' }}>
                                    {selectedVisit.cliente.nombre_comercial}
                                </div>
                                {selectedVisit.cliente.razon_social !== selectedVisit.cliente.nombre_comercial && (
                                    <div className="text-muted small mb-1">{selectedVisit.cliente.razon_social}</div>
                                )}
                                <div className="d-flex flex-wrap gap-2 small text-muted">
                                    <span><strong>RIF:</strong> {selectedVisit.cliente.rif_cedula}</span>
                                    {selectedVisit.cliente.telefono && <span>• <strong>Tlf:</strong> {selectedVisit.cliente.telefono}</span>}
                                    {selectedVisit.cliente.zona && <span>• <strong>Zona:</strong> {selectedVisit.cliente.zona}</span>}
                                </div>
                                {selectedVisit.cliente.direccion_fisica && (
                                    <div className="small text-muted mt-2">
                                        <i className="mdi mdi-map-marker text-danger me-1"></i>
                                        {selectedVisit.cliente.direccion_fisica}
                                    </div>
                                )}
                            </div>

                            {/* Visit Attributes */}
                            <Row className="g-3 mb-3">
                                <Col xs={6}>
                                    <div className="small text-muted mb-1">Ruta Asignada:</div>
                                    <div className="fw-semibold" style={{ fontSize: '0.88rem' }}>{selectedVisit.ruta_nombre}</div>
                                </Col>
                                <Col xs={6}>
                                    <div className="small text-muted mb-1">Tipo de Ruta:</div>
                                    <Badge bg={selectedVisit.tipo_ruta === 'merchandising' ? 'purple' : 'primary'} style={{ backgroundColor: selectedVisit.tipo_ruta === 'merchandising' ? '#7c3aed' : '#183eeb' }}>
                                        {selectedVisit.tipo_ruta === 'merchandising' ? 'Trade Marketing' : 'Preventa'}
                                    </Badge>
                                </Col>
                                <Col xs={6}>
                                    <div className="small text-muted mb-1">Responsable:</div>
                                    <div className="fw-semibold" style={{ fontSize: '0.88rem' }}>{selectedVisit.responsable.nombre_completo}</div>
                                </Col>
                                <Col xs={6}>
                                    <div className="small text-muted mb-1">Secuencia de Visita:</div>
                                    <div className="fw-semibold" style={{ fontSize: '0.88rem' }}>Visita #{selectedVisit.secuencia}</div>
                                </Col>
                            </Row>

                            {/* Status & Times */}
                            <div className="p-3 rounded mb-3 border" style={{ borderColor: isDark ? '#2a3759' : '#e2e8f0', backgroundColor: isDark ? '#141c33' : '#fafafa' }}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span className="small text-muted fw-bold">ESTATUS EN VIVO:</span>
                                    {getStatusBadge(selectedVisit)}
                                </div>

                                <div className="row g-2 pt-2 border-top" style={{ borderColor: isDark ? '#233052' : '#eeeeee', fontSize: '0.85rem' }}>
                                    <div className="col-6">
                                        <span className="text-muted">Hora de Check-In:</span><br />
                                        <strong>{selectedVisit.check_in_time || 'Aún no realizada'}</strong>
                                    </div>
                                    <div className="col-6">
                                        <span className="text-muted">Hora de Check-Out:</span><br />
                                        <strong>{selectedVisit.check_out_time || 'Aún no realizada'}</strong>
                                    </div>
                                </div>
                            </div>

                            {/* ═══════════════════════════════════════════════════════════ */}
                            {/* SECCIÓN SUPERVISORIA: VENDEDOR (PREVENTA COMERCIAL)       */}
                            {/* ═══════════════════════════════════════════════════════════ */}
                            {selectedVisit.tipo_ruta !== 'merchandising' && (
                                <div className="mb-3">
                                    {/* Reporte de No Venta (si aplica) */}
                                    {(selectedVisit.motivo_no_compra || ['no_compra', 'cerrado', 'cancelada'].includes(selectedVisit.estado) || ['No Compra', 'Local Cerrado', 'Visita Cancelada', 'Cancelada'].includes(selectedVisit.resultado_visita)) ? (
                                        <div 
                                            className="p-3 rounded border"
                                            style={{ 
                                                backgroundColor: isDark ? 'rgba(239, 68, 68, 0.12)' : '#fef2f2',
                                                borderColor: isDark ? 'rgba(239, 68, 68, 0.35)' : '#fecaca'
                                            }}
                                        >
                                            <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom" style={{ borderColor: isDark ? 'rgba(239, 68, 68, 0.25)' : '#fee2e2' }}>
                                                <div className="fw-bold d-flex align-items-center gap-2" style={{ color: isDark ? '#fca5a5' : '#b91c1c', fontSize: '0.9rem' }}>
                                                    <i className="mdi mdi-clipboard-text-off-outline fs-5"></i>
                                                    Reporte Oficial de No Venta
                                                </div>
                                                <span 
                                                    className="badge rounded-pill"
                                                    style={{ 
                                                        fontSize: '0.68rem',
                                                        backgroundColor: isDark ? 'rgba(239, 68, 68, 0.3)' : '#fee2e2',
                                                        color: isDark ? '#fca5a5' : '#991b1b',
                                                        border: isDark ? '1px solid rgba(239, 68, 68, 0.5)' : '1px solid #fca5a5',
                                                        fontWeight: '600'
                                                    }}
                                                >
                                                    Registrado desde App Móvil
                                                </span>
                                            </div>

                                            <div className="mb-2">
                                                <span className="small text-muted d-block mb-1">Motivo Reportado por el Vendedor:</span>
                                                <span className="badge bg-danger text-white px-2 py-1" style={{ fontSize: '0.85rem' }}>
                                                    {formatMotivoNoVenta(selectedVisit.motivo_no_compra || selectedVisit.resultado_visita)}
                                                </span>
                                            </div>

                                            {selectedVisit.observacion && (
                                                <div className="mt-2 pt-2 border-top small" style={{ borderColor: isDark ? 'rgba(239, 68, 68, 0.25)' : '#fee2e2', color: isDark ? '#cbd5e1' : '#475569' }}>
                                                    <div className="fw-semibold mb-1 d-flex align-items-center gap-1" style={{ color: isDark ? '#fca5a5' : '#b91c1c' }}>
                                                        <i className="mdi mdi-comment-text-outline"></i>
                                                        Observación del Vendedor:
                                                    </div>
                                                    <div className="p-2 rounded" style={{ backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.7)', border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #fecaca' }}>
                                                        {selectedVisit.observacion}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : selectedVisit.resultado_visita === 'Venta Exitosa' ? (
                                        <div 
                                            className="p-3 rounded border"
                                            style={{ 
                                                backgroundColor: isDark ? 'rgba(16, 185, 129, 0.12)' : '#ecfdf5',
                                                borderColor: isDark ? 'rgba(16, 185, 129, 0.35)' : '#a7f3d0'
                                            }}
                                        >
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <div className="fw-bold d-flex align-items-center gap-2" style={{ color: isDark ? '#6ee7b7' : '#047857', fontSize: '0.9rem' }}>
                                                    <i className="mdi mdi-check-decagram fs-5"></i>
                                                    Resultado Comercial: Venta Exitosa
                                                </div>
                                                <span 
                                                    className="badge rounded-pill"
                                                    style={{ 
                                                        fontSize: '0.68rem',
                                                        backgroundColor: isDark ? 'rgba(16, 185, 129, 0.3)' : '#d1fae5',
                                                        color: isDark ? '#6ee7b7' : '#065f46',
                                                        border: isDark ? '1px solid rgba(16, 185, 129, 0.5)' : '1px solid #6ee7b7',
                                                        fontWeight: '600'
                                                    }}
                                                >
                                                    Registrado en App Móvil
                                                </span>
                                            </div>
                                            <div className="small text-muted">
                                                {selectedVisit.observacion || 'Visita comercial completada y pedido registrado.'}
                                                {selectedVisit.duracion_minutos ? ` (Duración: ${selectedVisit.duracion_minutos} min)` : ''}
                                            </div>
                                        </div>
                                    ) : selectedVisit.estado === 'en_curso' ? (
                                        <div 
                                            className="p-3 rounded border"
                                            style={{ 
                                                backgroundColor: isDark ? 'rgba(59, 130, 246, 0.12)' : '#eff6ff',
                                                borderColor: isDark ? 'rgba(59, 130, 246, 0.35)' : '#bfdbfe'
                                            }}
                                        >
                                            <div className="d-flex align-items-center gap-2 fw-bold text-primary mb-1" style={{ fontSize: '0.9rem' }}>
                                                <i className="mdi mdi-account-clock fs-5"></i>
                                                En Sitio: Visita Comercial en Progreso
                                            </div>
                                            <div className="small text-muted">
                                                El vendedor registró check-in a las <strong>{selectedVisit.check_in_time}</strong> y está actualmente atendiendo al cliente.
                                            </div>
                                        </div>
                                    ) : (
                                        <div 
                                            className="p-3 rounded border"
                                            style={{ 
                                                backgroundColor: isDark ? '#10172d' : '#f8fafd',
                                                borderColor: isDark ? '#233052' : '#e2e8f0'
                                            }}
                                        >
                                            <div className="d-flex align-items-center gap-2 fw-bold text-muted mb-1" style={{ fontSize: '0.88rem' }}>
                                                <i className="mdi mdi-calendar-clock fs-5"></i>
                                                Visita Programada de Preventa
                                            </div>
                                            <div className="small text-muted">
                                                Programada en la secuencia #{selectedVisit.secuencia || 1}. Pendiente por ejecutar por el vendedor en la app móvil.
                                            </div>
                                        </div>
                                    )}

                                    {/* Evidencias Fotográficas de la Visita (HU20) */}
                                    {(selectedVisit.evidencia_entrada_url || selectedVisit.evidencia_anaquel_url) && (
                                        <div className="p-3 rounded border mt-3" style={{ borderColor: isDark ? '#2a3759' : '#e2e8f0', backgroundColor: isDark ? '#141c33' : '#fafafa' }}>
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <div className="fw-bold small d-flex align-items-center gap-1" style={{ color: isDark ? '#ffffff' : '#010028' }}>
                                                    <i className="mdi mdi-camera-outline text-primary fs-5"></i>
                                                    Evidencias Fotográficas de la Visita:
                                                </div>
                                                <span className="badge bg-primary-subtle text-primary border border-primary-subtle" style={{ fontSize: '0.65rem' }}>
                                                    S3 Bucket
                                                </span>
                                            </div>
                                            <div className="row g-2">
                                                {selectedVisit.evidencia_entrada_url && (
                                                    <div className={selectedVisit.evidencia_anaquel_url ? "col-6" : "col-12"}>
                                                        <div 
                                                            className="text-center p-2 rounded border h-100 d-flex flex-column justify-content-between" 
                                                            style={{ backgroundColor: isDark ? '#0e1529' : '#ffffff', borderColor: isDark ? '#233052' : '#e2e8f0', cursor: 'pointer' }}
                                                            onClick={() => {
                                                                setPreviewPhotoTitle(`Foto de Entrada (Frontis) - ${selectedVisit.cliente?.nombre || 'Cliente'}`);
                                                                setPreviewPhotoUrl(selectedVisit.evidencia_entrada_url);
                                                            }}
                                                            title="Clic para ampliar foto de entrada"
                                                        >
                                                            <div className="small fw-semibold mb-1 text-truncate" style={{ fontSize: '0.75rem', color: isDark ? '#93c5fd' : '#183eeb' }}>
                                                                <i className="mdi mdi-store-outline me-1"></i>Entrada / Fachada
                                                            </div>
                                                            <img 
                                                                src={selectedVisit.evidencia_entrada_url} 
                                                                alt="Foto Entrada" 
                                                                className="img-fluid rounded" 
                                                                style={{ maxHeight: '130px', objectFit: 'cover', width: '100%' }}
                                                            />
                                                            <div className="small text-muted mt-1 d-flex align-items-center justify-content-center gap-1" style={{ fontSize: '0.7rem' }}>
                                                                <i className="mdi mdi-magnify-plus-outline text-primary"></i>
                                                                <span>Ampliar</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {selectedVisit.evidencia_anaquel_url && (
                                                    <div className={selectedVisit.evidencia_entrada_url ? "col-6" : "col-12"}>
                                                        <div 
                                                            className="text-center p-2 rounded border h-100 d-flex flex-column justify-content-between" 
                                                            style={{ backgroundColor: isDark ? '#0e1529' : '#ffffff', borderColor: isDark ? '#233052' : '#e2e8f0', cursor: 'pointer' }}
                                                            onClick={() => {
                                                                setPreviewPhotoTitle(`Foto de Exhibición / Anaquel - ${selectedVisit.cliente?.nombre || 'Cliente'}`);
                                                                setPreviewPhotoUrl(selectedVisit.evidencia_anaquel_url);
                                                            }}
                                                            title="Clic para ampliar foto de anaquel"
                                                        >
                                                            <div className="small fw-semibold mb-1 text-truncate" style={{ fontSize: '0.75rem', color: isDark ? '#86efac' : '#15803d' }}>
                                                                <i className="mdi mdi-view-grid-outline me-1"></i>Anaquel / Vitrina
                                                            </div>
                                                            <img 
                                                                src={selectedVisit.evidencia_anaquel_url} 
                                                                alt="Foto Anaquel" 
                                                                className="img-fluid rounded" 
                                                                style={{ maxHeight: '130px', objectFit: 'cover', width: '100%' }}
                                                            />
                                                            <div className="small text-muted mt-1 d-flex align-items-center justify-content-center gap-1" style={{ fontSize: '0.7rem' }}>
                                                                <i className="mdi mdi-magnify-plus-outline text-primary"></i>
                                                                <span>Ampliar</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ═══════════════════════════════════════════════════════════ */}
                            {/* SECCIÓN SUPERVISORIA: MERCADERISTA (TRADE MARKETING)      */}
                            {/* ═══════════════════════════════════════════════════════════ */}
                            {selectedVisit.tipo_ruta === 'merchandising' && (
                                <div className="d-flex flex-column gap-3 mb-3">
                                    {/* Incidencia Operativa del Mercaderista (si aplica) */}
                                    {(selectedVisit.motivo_no_compra || ['no_compra', 'cerrado', 'cancelada'].includes(selectedVisit.estado) || ['No Compra', 'Local Cerrado', 'Visita Cancelada', 'Cancelada'].includes(selectedVisit.resultado_visita)) ? (
                                        <div 
                                            className="p-3 rounded border"
                                            style={{ 
                                                backgroundColor: isDark ? 'rgba(245, 158, 11, 0.12)' : '#fffbeb',
                                                borderColor: isDark ? 'rgba(245, 158, 11, 0.35)' : '#fde68a'
                                            }}
                                        >
                                            <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom" style={{ borderColor: isDark ? 'rgba(245, 158, 11, 0.25)' : '#fef3c7' }}>
                                                <div className="fw-bold d-flex align-items-center gap-2" style={{ color: isDark ? '#fde047' : '#b45309', fontSize: '0.9rem' }}>
                                                    <i className="mdi mdi-alert-octagon-outline fs-5"></i>
                                                    Incidencia Operativa de Trade Marketing
                                                </div>
                                                <span 
                                                    className="badge rounded-pill"
                                                    style={{ 
                                                        fontSize: '0.68rem',
                                                        backgroundColor: isDark ? 'rgba(245, 158, 11, 0.3)' : '#fef3c7',
                                                        color: isDark ? '#fde047' : '#92400e',
                                                        border: isDark ? '1px solid rgba(245, 158, 11, 0.5)' : '1px solid #fde68a',
                                                        fontWeight: '600'
                                                    }}
                                                >
                                                    Reportado desde App Móvil
                                                </span>
                                            </div>

                                            <div className="mb-2">
                                                <span className="small text-muted d-block mb-1">Motivo / Incidencia Reportada:</span>
                                                <span className="badge bg-warning text-dark px-2 py-1" style={{ fontSize: '0.85rem' }}>
                                                    {formatMotivoNoVenta(selectedVisit.motivo_no_compra || selectedVisit.resultado_visita)}
                                                </span>
                                            </div>

                                            {selectedVisit.observacion && (
                                                <div className="mt-2 pt-2 border-top small" style={{ borderColor: isDark ? 'rgba(245, 158, 11, 0.25)' : '#fef3c7', color: isDark ? '#cbd5e1' : '#475569' }}>
                                                    <div className="fw-semibold mb-1 d-flex align-items-center gap-1" style={{ color: isDark ? '#fcd34d' : '#b45309' }}>
                                                        <i className="mdi mdi-comment-text-outline"></i>
                                                        Observación del Mercaderista:
                                                    </div>
                                                    <div className="p-2 rounded" style={{ backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.7)', border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #fef3c7' }}>
                                                        {selectedVisit.observacion}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : selectedVisit.resultado_visita === 'Auditoría Completada' ? (
                                        <div 
                                            className="p-3 rounded border"
                                            style={{ 
                                                backgroundColor: isDark ? 'rgba(124, 58, 237, 0.12)' : '#f5f3ff',
                                                borderColor: isDark ? 'rgba(124, 58, 237, 0.35)' : '#ddd6fe'
                                            }}
                                        >
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <div className="fw-bold d-flex align-items-center gap-2" style={{ color: isDark ? '#c4b5fd' : '#6d28d9', fontSize: '0.9rem' }}>
                                                    <i className="mdi mdi-store-check fs-5"></i>
                                                    Auditoría de Trade Marketing Completada
                                                </div>
                                                <span 
                                                    className="badge rounded-pill"
                                                    style={{ 
                                                        fontSize: '0.68rem',
                                                        backgroundColor: isDark ? 'rgba(124, 58, 237, 0.3)' : '#ede9fe',
                                                        color: isDark ? '#c4b5fd' : '#5b21b6',
                                                        border: isDark ? '1px solid rgba(124, 58, 237, 0.5)' : '1px solid #c4b5fd',
                                                        fontWeight: '600'
                                                    }}
                                                >
                                                    Reportado en App Móvil
                                                </span>
                                            </div>
                                            <div className="small text-muted">
                                                {selectedVisit.observacion || 'Organización de anaquel, verificación de planograma y precios de competencia finalizada.'}
                                                {selectedVisit.duracion_minutos ? ` (Duración: ${selectedVisit.duracion_minutos} min)` : ''}
                                            </div>
                                        </div>
                                    ) : selectedVisit.estado === 'en_curso' ? (
                                        <div 
                                            className="p-3 rounded border"
                                            style={{ 
                                                backgroundColor: isDark ? 'rgba(59, 130, 246, 0.12)' : '#eff6ff',
                                                borderColor: isDark ? 'rgba(59, 130, 246, 0.35)' : '#bfdbfe'
                                            }}
                                        >
                                            <div className="d-flex align-items-center gap-2 fw-bold text-primary mb-1" style={{ fontSize: '0.9rem' }}>
                                                <i className="mdi mdi-store-clock fs-5"></i>
                                                En Sitio: Mercaderista Auditando Anaquel
                                            </div>
                                            <div className="small text-muted">
                                                El mercaderista inició check-in a las <strong>{selectedVisit.check_in_time}</strong> y está ejecutando labores de exhibición en tienda.
                                            </div>
                                        </div>
                                    ) : (
                                        <div 
                                            className="p-3 rounded border"
                                            style={{ 
                                                backgroundColor: isDark ? '#10172d' : '#f8fafd',
                                                borderColor: isDark ? '#233052' : '#e2e8f0'
                                            }}
                                        >
                                            <div className="d-flex align-items-center gap-2 fw-bold text-muted mb-1" style={{ fontSize: '0.88rem' }}>
                                                <i className="mdi mdi-calendar-clock fs-5"></i>
                                                Visita Programada de Trade Marketing
                                            </div>
                                            <div className="small text-muted">
                                                Programada en la secuencia #{selectedVisit.secuencia || 1}. Pendiente por ejecutar por el mercaderista en la app móvil.
                                            </div>
                                        </div>
                                    )}

                                    {/* Quiebres de Stock Detectados */}
                                    {Array.isArray(selectedVisit.merchandising_report?.quiebres_stock) && selectedVisit.merchandising_report.quiebres_stock.length > 0 && (
                                        <div 
                                            className="p-3 rounded border"
                                            style={{ 
                                                backgroundColor: isDark ? 'rgba(239, 68, 68, 0.1)' : '#fef2f2',
                                                borderColor: isDark ? 'rgba(239, 68, 68, 0.3)' : '#fecaca'
                                            }}
                                        >
                                            <div className="fw-bold text-danger mb-2 d-flex align-items-center gap-1" style={{ fontSize: '0.88rem' }}>
                                                <i className="mdi mdi-package-variant-closed-remove fs-5"></i>
                                                Quiebres de Stock Reportados en Anaquel:
                                            </div>
                                            <div className="d-flex flex-wrap gap-2">
                                                {selectedVisit.merchandising_report.quiebres_stock.map((item, qIdx) => (
                                                    <span key={qIdx} className="badge bg-danger text-white" style={{ fontSize: '0.78rem' }}>
                                                        {typeof item === 'string' ? item : (item.producto || item.sku || 'Producto agotado')}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Evidencia Fotográfica (Antes vs. Después) */}
                                    {(selectedVisit.merchandising_report?.foto_antes_url || selectedVisit.merchandising_report?.foto_despues_url || selectedVisit.evidencia_anaquel_url) && (
                                        <div className="p-3 rounded border" style={{ borderColor: isDark ? '#2a3759' : '#e2e8f0', backgroundColor: isDark ? '#141c33' : '#fafafa' }}>
                                            <div className="fw-bold small mb-2 d-flex align-items-center gap-1" style={{ color: isDark ? '#ffffff' : '#010028' }}>
                                                <i className="mdi mdi-camera-outline text-primary fs-5"></i>
                                                Evidencia Fotográfica de Exhibición:
                                            </div>
                                            <Row className="g-2">
                                                {(selectedVisit.merchandising_report?.foto_antes_url || selectedVisit.evidencia_anaquel_url) && (
                                                    <Col xs={6}>
                                                        <div 
                                                            className="text-center p-2 rounded border" 
                                                            style={{ backgroundColor: isDark ? '#0e1529' : '#ffffff', borderColor: isDark ? '#233052' : '#e2e8f0', cursor: 'pointer' }}
                                                            onClick={() => {
                                                                setPreviewPhotoTitle(`Evidencia Antes / Anaquel - ${selectedVisit.cliente?.nombre || 'Cliente'}`);
                                                                setPreviewPhotoUrl(selectedVisit.merchandising_report?.foto_antes_url || selectedVisit.evidencia_anaquel_url);
                                                            }}
                                                            title="Clic para ampliar foto"
                                                        >
                                                            <span className="badge bg-secondary mb-1" style={{ fontSize: '0.65rem' }}>ANTES</span>
                                                            <img 
                                                                src={selectedVisit.merchandising_report?.foto_antes_url || selectedVisit.evidencia_anaquel_url} 
                                                                alt="Antes" 
                                                                className="img-fluid rounded" 
                                                                style={{ maxHeight: '130px', objectFit: 'cover', width: '100%' }}
                                                            />
                                                        </div>
                                                    </Col>
                                                )}
                                                {selectedVisit.merchandising_report?.foto_despues_url && (
                                                    <Col xs={6}>
                                                        <div 
                                                            className="text-center p-2 rounded border" 
                                                            style={{ backgroundColor: isDark ? '#0e1529' : '#ffffff', borderColor: isDark ? '#233052' : '#e2e8f0', cursor: 'pointer' }}
                                                            onClick={() => {
                                                                setPreviewPhotoTitle(`Evidencia Después - ${selectedVisit.cliente?.nombre || 'Cliente'}`);
                                                                setPreviewPhotoUrl(selectedVisit.merchandising_report.foto_despues_url);
                                                            }}
                                                            title="Clic para ampliar foto"
                                                        >
                                                            <span className="badge bg-success mb-1" style={{ fontSize: '0.65rem' }}>DESPUÉS (ORDENADO)</span>
                                                            <img 
                                                                src={selectedVisit.merchandising_report.foto_despues_url} 
                                                                alt="Después" 
                                                                className="img-fluid rounded" 
                                                                style={{ maxHeight: '130px', objectFit: 'cover', width: '100%' }}
                                                            />
                                                        </div>
                                                    </Col>
                                                )}
                                            </Row>
                                        </div>
                                    )}

                                    {/* Material POP */}
                                    {selectedVisit.merchandising_report?.material_pop && typeof selectedVisit.merchandising_report.material_pop === 'object' && (
                                        <div className="p-3 rounded border small" style={{ borderColor: isDark ? '#2a3759' : '#e2e8f0', backgroundColor: isDark ? '#141c33' : '#fafafa', color: isDark ? '#cbd5e1' : '#475569' }}>
                                            <strong className="d-block mb-1 text-primary">Estado de Material POP:</strong>
                                            <div>Exhibidor: <span className="fw-semibold text-capitalize">{selectedVisit.merchandising_report.material_pop.exhibidor_principal?.replace('_', ' ') || 'Verificado'}</span></div>
                                            {selectedVisit.merchandising_report.material_pop.observacion && (
                                                <div className="mt-1 text-muted">{selectedVisit.merchandising_report.material_pop.observacion}</div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ═══════════════════════════════════════════════════════════ */}
                            {/* ACCIONES DE SUPERVISIÓN Y GESTIÓN DE EXCEPCIONES        */}
                            {/* ═══════════════════════════════════════════════════════════ */}
                            {(() => {
                                const isEffectiveSuccess = selectedVisit.resultado_visita === 'Venta Exitosa' || selectedVisit.resultado_visita === 'Auditoría Completada';

                                if (isEffectiveSuccess) {
                                    return (
                                        <div className="p-3 rounded border mb-3" style={{ borderColor: isDark ? 'rgba(16, 185, 129, 0.4)' : '#a7f3d0', backgroundColor: isDark ? 'rgba(16, 185, 129, 0.1)' : '#ecfdf5' }}>
                                            <div className="d-flex align-items-center gap-2" style={{ color: isDark ? '#6ee7b7' : '#065f46', fontWeight: '600', fontSize: '0.88rem' }}>
                                                <i className="mdi mdi-check-circle-outline fs-5"></i>
                                                Visita Concretada Exitosamente
                                            </div>
                                            <p className="small mb-0 mt-1" style={{ color: isDark ? '#a7f3d0' : '#047857' }}>
                                                Esta visita se completó de manera efectiva ({selectedVisit.resultado_visita}). No requiere reorganización de ruta.
                                            </p>
                                        </div>
                                    );
                                }

                                return (
                                    <div className="p-3 rounded border mb-3" style={{ borderColor: isDark ? '#2a3759' : '#cbd5e1', backgroundColor: isDark ? '#10172d' : '#f8fafc' }}>
                                        <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom" style={{ borderColor: isDark ? '#233052' : '#e2e8f0' }}>
                                            <div className="fw-bold d-flex align-items-center gap-2" style={{ color: isDark ? '#ffffff' : '#010028', fontSize: '0.9rem' }}>
                                                <i className="mdi mdi-shield-account text-primary fs-5"></i>
                                                Supervisión y Reorganización de Ruta
                                            </div>
                                            <Badge bg="secondary" style={{ fontSize: '0.68rem' }}>Control Supervisor</Badge>
                                        </div>

                                        <p className="text-muted small mb-3">
                                            Esta visita no se concretó ({selectedVisit.resultado_visita || (selectedVisit.is_past ? 'No Realizada' : 'Pendiente')}). Para mantener el orden y la secuencia de visitas en la cartera, reorganiza los días o reasigna al cliente centralizadamente en el Planificador de Rutas.
                                        </p>

                                        <div className="d-flex flex-wrap gap-2 align-items-center">
                                            {/* Redirección al Planificador de Rutas */}
                                            <Link
                                                to={`/general-pages/route-planner?route_id=${selectedVisit.ruta_id}&client_id=${selectedVisit.cliente_id || selectedVisit.cliente?.id}`}
                                                className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
                                                style={{ borderRadius: '8px', fontSize: '0.82rem', fontWeight: '600' }}
                                                onClick={() => setShowDetailModal(false)}
                                            >
                                                <i className="mdi mdi-map-marker-path me-1"></i>
                                                Reorganizar en Planificador de Rutas
                                            </Link>

                                            {/* Botón / Badge Aprobar Justificación */}
                                            {(selectedVisit.motivo_no_compra || ['no_compra', 'cerrado', 'cancelada', 'no_realizada'].includes(selectedVisit.estado) || ['No Compra', 'Local Cerrado', 'Visita Cancelada', 'Cancelada', 'No Realizada'].includes(selectedVisit.resultado_visita)) && (
                                                selectedVisit.justificacion_aprobada ? (
                                                    <span 
                                                        className="badge d-inline-flex align-items-center gap-1 p-2" 
                                                        style={{ 
                                                            backgroundColor: isDark ? 'rgba(16, 185, 129, 0.2)' : '#d1fae5', 
                                                            color: isDark ? '#6ee7b7' : '#065f46',
                                                            border: '1px solid #10b981',
                                                            fontSize: '0.78rem'
                                                        }}
                                                    >
                                                        <i className="mdi mdi-check-decagram fs-6 text-success"></i>
                                                        Justificación Aprobada por {selectedVisit.justificacion_aprobada_by || 'Supervisor'}
                                                    </span>
                                                ) : (
                                                    <Button
                                                        variant="outline-success"
                                                        size="sm"
                                                        style={{ borderRadius: '8px', fontSize: '0.82rem', fontWeight: '600' }}
                                                        onClick={() => handleApproveJustification(selectedVisit.id)}
                                                        disabled={approvingJustification}
                                                    >
                                                        {approvingJustification ? (
                                                            <>
                                                                <Spinner size="sm" animation="border" className="me-1" />
                                                                Aprobando...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="mdi mdi-check-circle-outline me-1"></i>
                                                                Aprobar Justificación Oficial
                                                            </>
                                                        )}
                                                    </Button>
                                                )
                                            )}
                                        </div>
                                    </div>
                                );
                            })()}

                            <div className="d-flex justify-content-end">
                                <Button variant="secondary" onClick={() => setShowDetailModal(false)} style={{ borderRadius: '8px' }}>
                                    {t('btn_close', 'Cerrar')}
                                </Button>
                            </div>
                        </div>
                    )}
                </Modal.Body>
            </Modal>

            {/* Modal Lightbox / Zoom de Foto de Evidencia (HU20) */}
            <Modal show={!!previewPhotoUrl} onHide={() => setPreviewPhotoUrl(null)} centered size="lg">
                <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: isDark ? '#10172d' : '#00024f', color: '#ffffff', borderBottom: '3px solid #183eeb' }}>
                    <Modal.Title style={{ color: '#ffffff', fontWeight: 'bold', fontSize: '1rem' }}>
                        <i className="mdi mdi-camera-outline me-2 text-info"></i>
                        {previewPhotoTitle}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-3 text-center" style={{ backgroundColor: isDark ? '#0b1120' : '#f8fafc' }}>
                    {previewPhotoUrl && (
                        <img 
                            src={previewPhotoUrl} 
                            alt={previewPhotoTitle} 
                            className="img-fluid rounded shadow"
                            style={{ maxHeight: '72vh', maxWidth: '100%', objectFit: 'contain' }}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer style={{ backgroundColor: isDark ? '#161e38' : '#ffffff', borderTop: isDark ? '1px solid #233052' : '1px solid #e2e8f0' }}>
                    {previewPhotoUrl && (
                        <a 
                            href={previewPhotoUrl} 
                            target="_blank" 
                            rel="noreferrer" 
                            className="btn btn-outline-primary btn-sm me-auto"
                            style={{ borderRadius: '6px' }}
                        >
                            <i className="mdi mdi-open-in-new me-1"></i>
                            Abrir original
                        </a>
                    )}
                    <Button variant="secondary" size="sm" onClick={() => setPreviewPhotoUrl(null)} style={{ borderRadius: '6px' }}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AgendaMonitor;
